import type {
	MenuItemsAPIResponse,
	ProcessedSuggestionSection,
} from '@atlassian/navigation-bff-data-provider/universal-create';

import type {
	AppDefinedMenuSection,
	MenuSectionV2,
	RenderableMenuSectionData,
	SuggestionMenuSection,
} from '../../../../types/menuV2';

import { filterNativeItemsOnly, getHydrateMenuItems } from './getHydrateMenuItems';

/**
 * Type guard to check if a section is an app-defined section.
 */
export const isAppDefinedSection = (section: MenuSectionV2): section is AppDefinedMenuSection =>
	section.type === 'appDefinedSection';

/**
 * Type guard to check if a section is a suggestion section.
 */
export const isSuggestionSection = (section: MenuSectionV2): section is SuggestionMenuSection =>
	section.type === 'suggestionSection';

/**
 * Filters sections to get only app-defined sections.
 */
export const getAppDefinedSections = (sections: MenuSectionV2[]): AppDefinedMenuSection[] =>
	sections.filter(isAppDefinedSection);

/**
 * Processes a single suggestion section by matching it with a response section.
 * Suggestion sections contain directly renderable items from the backend.
 * Only cross-app items are included (native item references are filtered out).
 */
const processSuggestionSection = (
	configSection: SuggestionMenuSection,
	responseSection: ProcessedSuggestionSection | undefined,
): RenderableMenuSectionData | null => {
	if (!responseSection || responseSection.items.length === 0) {
		return null;
	}
	return {
		type: 'suggestionSection',
		heading: configSection.heading,
		foldable: configSection.foldable,
		items: responseSection.items,
	};
};

/**
 * Processes app-defined sections by hydrating them with response data.
 */
const processAppDefinedSections = (
	configSections: AppDefinedMenuSection[],
	data: MenuItemsAPIResponse,
): RenderableMenuSectionData[] => {
	return getHydrateMenuItems(data, configSections);
};

/**
 * Computes renderable sections based on data state and error conditions.
 *
 * When data is available:
 * - Suggestion sections: Use items directly from API response (already renderable)
 * - App-defined sections: Hydrate items by matching response with config
 *
 * When there's an error or no data:
 * - Suggestion sections: Cannot be shown (require backend data)
 * - App-defined sections: Show native items only as fallback
 */
export const computeRenderableSections = (
	sections: MenuSectionV2[],
	data: MenuItemsAPIResponse | undefined,
	hasError: boolean,
): RenderableMenuSectionData[] => {
	// Error/no data case: return only native items from app-defined sections
	if (hasError || !data) {
		const appDefinedSections = getAppDefinedSections(sections);
		return filterNativeItemsOnly(appDefinedSections);
	}

	// Separate sections by type for processing
	const suggestionResponseSections = data.sections.filter(
		(section): section is ProcessedSuggestionSection => section.type === 'suggestionSection',
	);
	const appDefinedConfigSections = getAppDefinedSections(sections);
	const processedAppSections = processAppDefinedSections(appDefinedConfigSections, data);

	// Process sections in original order, matching with response data
	let suggestionIndex = 0;
	let appSectionIndex = 0;
	return sections
		.map((configSection) => {
			if (isSuggestionSection(configSection)) {
				const responseSection = suggestionResponseSections[suggestionIndex++];
				return processSuggestionSection(configSection, responseSection);
			}
			if (isAppDefinedSection(configSection)) {
				return processedAppSections[appSectionIndex++];
			}
			return null;
		})
		.filter((section): section is RenderableMenuSectionData => section !== null);
};

/**
 * Calculates the total number of items across all sections.
 */
export const computeTotalItemCount = (sections: RenderableMenuSectionData[]): number =>
	sections.reduce((total, section) => total + section.items.length, 0);

/**
 * Section metadata containing pre-computed total count and start indices.
 */
export interface SectionMetadata {
	startIndices: number[];
	totalCount: number;
}

/**
 * Computes all section metadata (total count and start indices) in a single pass.
 * Optimized to compute both values in one reduce operation with O(n) complexity.
 *
 * @param sections - The renderable menu sections to analyze
 * @returns Object containing totalCount and array of startIndices for each section
 *
 * @example
 * const metadata = computeSectionMetadata(sections);
 * // Use in render:
 * // totalMenuItemsCount={metadata.totalCount}
 * // sectionStartIndex={metadata.startIndices[sectionIndex]}
 */
export const computeSectionMetadata = (sections: RenderableMenuSectionData[]): SectionMetadata => {
	let totalCount = 0;
	const startIndices: number[] = [];

	sections.forEach((section) => {
		startIndices.push(totalCount);
		totalCount += section.items.length;
	});

	return { totalCount, startIndices };
};
